'use client';

import dynamic from 'next/dynamic';

import type { PropsWithChildren } from 'react';
import { useState } from 'react';

import { HydrationBoundary, QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

import type { ProductVariantInventory } from '@libs/algolia';

const ReactQueryDevtools =
    process.env.NODE_ENV === 'development'
        ? dynamic(
              () =>
                  import('@tanstack/react-query-devtools').then(
                      mod => mod.ReactQueryDevtools
                  ),
              { ssr: false }
          )
        : () => null;

type QueryProviderProps = {
    inventories: ProductVariantInventory;
} & PropsWithChildren;

export function QueryProvider({
    children,
    inventories,
}: Readonly<QueryProviderProps>) {
    // 1) Create a TanStack QueryClient (only once).
    const [queryClient] = useState(
        new QueryClient({
            defaultOptions: {
                queries: {
                    retry: 0,
                    // Keep cache 24 hrs - data is considered fresh for 24 hrs,
                    // and during this period, reusing the cache will not fetch new data.
                    // staleTime: 24 * 60 * 60 * 1000, // 24 hrs

                    // After the data has become unused (e.g. the component is unmounted),
                    // it will be stored for another 24 hrs in the cache before being deleted.
                    gcTime: 24 * 60 * 60 * 1000, // 24 hrs
                    refetchOnMount: true,
                    refetchOnWindowFocus: false,
                },
            },
        })
    );

    // 2) Create the persister for localStorage
    const persister = createSyncStoragePersister({
        storage:
            typeof window !== 'undefined' ? window.localStorage : undefined,
    });

    // 3) Default initial data from the server, it could be prefetched data
    const dehydratedState = {
        queries: [
            {
                queryKey: ['inventories-initial-data'],
                state: { data: inventories, status: 'success' },
            },
        ],
    };

    // Described in the README.md file
    // The buster parameter in persistOptions is responsible for forcibly dumping (cache busting) the old cache.
    // It could be the release version - process.env.CACHE_BUSTER_RELEASE=3.1.1
    const busterValue = '';

    return (
        <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
                persister,
                maxAge: 24 * 60 * 60 * 1000,
                buster: busterValue,
            }}
        >
            <HydrationBoundary state={dehydratedState}>
                {children}
                <ReactQueryDevtools
                    initialIsOpen={false}
                    buttonPosition="bottom-left"
                />
            </HydrationBoundary>
        </PersistQueryClientProvider>
    );
}
